// Authentification : lien magique ou code à 6 chiffres reçu par e-mail.
import type { SupabaseClient } from '@supabase/supabase-js';

export interface AuthUser {
  id: string;
  email: string;
}

export interface Auth {
  current(): Promise<AuthUser | null>;
  onChange(cb: (u: AuthUser | null) => void): () => void;
  requestCode(email: string): Promise<void>;
  verifyCode(email: string, code: string): Promise<void>;
  signOut(): Promise<void>;
}

function frenchAuthError(e: unknown): Error {
  const msg = e instanceof Error ? e.message : String(e ?? '');
  if (/signups? not allowed|not allowed for otp/i.test(msg)) return new Error('Cette adresse n’est pas autorisée à se connecter.');
  if (/rate limit|too many|over_email_send_rate_limit/i.test(msg)) return new Error('Trop de demandes : patientez quelques minutes avant de redemander un code.');
  if (/invalid|expired|otp_expired|token has expired/i.test(msg)) return new Error('Code incorrect ou expiré. Demandez-en un nouveau.');
  if (/fetch|network/i.test(msg)) return new Error('Le serveur n’a pas répondu (réseau).');
  return new Error(msg || 'Connexion impossible.');
}

export class SupabaseAuth implements Auth {
  constructor(private readonly client: SupabaseClient) {}

  async current(): Promise<AuthUser | null> {
    const { data, error } = await this.client.auth.getSession();
    if (error) throw frenchAuthError(error);
    const u = data.session?.user;
    return u ? { id: u.id, email: u.email ?? '' } : null;
  }

  onChange(cb: (u: AuthUser | null) => void): () => void {
    const { data } = this.client.auth.onAuthStateChange((_event, session) => {
      const u = session?.user;
      cb(u ? { id: u.id, email: u.email ?? '' } : null);
    });
    return () => data.subscription.unsubscribe();
  }

  async requestCode(email: string): Promise<void> {
    const redirect = typeof location !== 'undefined' ? location.origin + location.pathname : undefined;
    const { error } = await this.client.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false, ...(redirect ? { emailRedirectTo: redirect } : {}) },
    });
    if (error) throw frenchAuthError(error);
  }

  async verifyCode(email: string, code: string): Promise<void> {
    const { error } = await this.client.auth.verifyOtp({ email, token: code.replace(/\s+/g, ''), type: 'email' });
    if (error) throw frenchAuthError(error);
  }

  async signOut(): Promise<void> {
    const { error } = await this.client.auth.signOut();
    if (error) throw frenchAuthError(error);
  }
}

/** Développement et tests : un utilisateur fictif, toujours connecté. Jamais en production. */
export class MemoryAuth implements Auth {
  private user: AuthUser | null;
  private listeners = new Set<(u: AuthUser | null) => void>();
  constructor(user: AuthUser | null = { id: 'dev-user', email: 'dev@localhost' }) { this.user = user; }
  async current(): Promise<AuthUser | null> { return this.user; }
  onChange(cb: (u: AuthUser | null) => void): () => void { this.listeners.add(cb); return () => this.listeners.delete(cb); }
  async requestCode(): Promise<void> { /* rien à envoyer */ }
  async verifyCode(email: string): Promise<void> { this.user = { id: 'dev-user', email }; this.listeners.forEach((l) => l(this.user)); }
  async signOut(): Promise<void> { this.user = null; this.listeners.forEach((l) => l(null)); }
}
